/*
 * De3eHelft – auxiliary consent-aware campaign attribution (no Meta events).
 * Private cart attributes are visible in Shopify admin but not at checkout.
 * No click ID or personally identifying information is captured.
 */
(() => {
  'use strict';
  const SESSION_KEY = 'd3_campaign_attribution_v2';
  const SENT_KEY = 'd3_campaign_cart_sync_v2';
  const attributeNames = {
    utm_source: '__d3_source',
    utm_medium: '__d3_medium',
    utm_campaign: '__d3_campaign',
    utm_term: '__d3_adset',
    utm_content: '__d3_ad'
  };
  const campaignFields = Object.keys(attributeNames);
  const isCart = /^\/cart\/?$/i.test(window.location.pathname);
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  let busy = false;
  let rerun = false;

  const safeGet = (key) => {
    try { return window.sessionStorage.getItem(key); }
    catch (_) { return null; }
  };
  const safeSet = (key, value) => {
    try { window.sessionStorage.setItem(key, value); }
    catch (_) {}
  };
  const safeRemove = (key) => {
    try { window.sessionStorage.removeItem(key); }
    catch (_) {}
  };
  const getConsent = () => {
    try { return window.Shopify?.customerPrivacy?.currentVisitorConsent?.()?.marketing || null; }
    catch (_) { return null; }
  };
  const currentCampaign = () => {
    try {
      const data = JSON.parse(safeGet(SESSION_KEY) || 'null');
      return data && typeof data === 'object' && data.utm_campaign ? data : null;
    } catch (_) { return null; }
  };

  function capture() {
    const query = new URLSearchParams(window.location.search);
    if (!query.get('utm_campaign')) return;
    const campaign = {};
    campaignFields.forEach((key) => {
      const value = query.get(key);
      if (value && value.length <= 180) campaign[key] = value;
    });
    if (campaign.utm_campaign) safeSet(SESSION_KEY, JSON.stringify(campaign));
  }

  async function syncCart(allowed) {
    if (!isCart && !document.getElementById('d3-floating-cart')?.open) return;
    if (busy) { rerun = true; return; }
    const campaign = allowed ? currentCampaign() : null;
    // Clear any previously saved private campaign fields when consent is denied.
    if (allowed && !campaign) return;
    busy = true;
    try {
      const response = await fetch(root + 'cart.js', {credentials: 'same-origin'});
      if (!response.ok) return;
      const cart = await response.json();
      if (!cart.items || !cart.items.length) return;
      const data = campaign || {};
      const signature = [cart.token, allowed ? 'granted' : 'denied', ...campaignFields.map(k => data[k] || '')].join(':');
      if (safeGet(SENT_KEY) === signature) return;
      const attributes = {...(cart.attributes || {})};
      for (const [utmKey, attr] of Object.entries(attributeNames)) {
        // Empty values remove previously saved private attributes.
        attributes[attr] = data[utmKey] || '';
      }
      const updated = await fetch(root + 'cart/update.js', {
        method: 'POST', credentials: 'same-origin',
        headers: {'Content-Type': 'application/json', Accept: 'application/json'},
        body: JSON.stringify({attributes})
      });
      if (updated.ok) safeSet(SENT_KEY, signature);
    } catch (_) {
      // Never prevent native checkout, Shopify analytics or Meta CAPI.
    } finally {
      busy = false;
      if (rerun) {
        rerun = false;
        const consent = getConsent();
        if (consent === 'yes' || consent === 'no') void syncCart(consent === 'yes');
      }
    }
  }

  function syncConsent() {
    const consent = getConsent();
    if (consent !== 'yes' && consent !== 'no') return;
    if (consent === 'yes') capture();
    else safeRemove(SESSION_KEY);
    void syncCart(consent === 'yes');
  }

  function initialize(attempt = 0) {
    if (typeof window.Shopify?.customerPrivacy?.currentVisitorConsent === 'function') syncConsent();
    else if (attempt < 40) window.setTimeout(() => initialize(attempt + 1), 250);
  }

  document.addEventListener('visitorConsentCollected', syncConsent);
  document.addEventListener('d3:cart:updated', syncConsent);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize(), {once:true});
  } else initialize();
})();
